import {
  FC,
  memo,
  useEffect,
  useState,
  useRef,
  useCallback,
  ChangeEvent,
} from 'react';
import { Search, XIcon } from 'lucide-react';
import styled from 'styled-components';

import { useScroll } from '@hooks/useScroll';
import { useSearch } from '@providers/SearchProvider';
import { useShowsIds, useShowsMayloadMore } from '@redux/selectors/search';
import { useLazyGetAllShowsQuery } from '@redux/queries/show';
import { emptyText, SKEW_ANGLE } from '@utils/constants';
import { TEXT_INPUT_PLACEHOLDERS } from '@utils/constants/form';
import DefaultInput from '@components/fields/input';
import SkewedButton from '@components/buttons/SkewedButton';
import Loader from '@components/loader';
import ShowCard from '@components/show/card';
import { ShowsContainer, EmptyState } from '@pages/home/components/tabs/styled';
import { useScreenSizes } from '@hooks/useScreenSizes';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 0;
`;

const StickyHeader = styled.div`
  width: 100%;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: ${({ theme }) => theme.colors.black};
    padding-top: max(0px, env(safe-area-inset-top));
  }
`;

const FiltersContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
  padding-inline: 8px;
  padding-block: 4px;
  overflow-x: auto;
  flex-shrink: 0;
  ${({ theme }) => theme.scrollbar.horizontal}

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-inline: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: flex-start;
  }
`;

const InputContainer = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: center;
  padding-inline: 16px;
  margin: 8px auto;
  position: relative;
`;

const SearchInput = styled(DefaultInput)`
  width: 100%;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  right: 16px;
  color: ${({ theme }) => theme.colors.grey[300]};
  pointer-events: none;
  transform: skewX(${SKEW_ANGLE}deg);
`;

const CloseIcon = styled(XIcon)`
  position: absolute;
  right: 16px;
  color: ${({ theme }) => theme.colors.grey[300]};
  cursor: pointer;
  transform: skewX(${SKEW_ANGLE}deg);
`;

const FilterButton = styled(SkewedButton)`
  min-width: 120px;
  flex-shrink: 0;
`;

const ShowsWrapper = styled.div`
  flex: 1;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const filters = [
  {
    id: 'all',
    label: 'All',
  },
  {
    id: 'live',
    label: 'Live Now',
  },
  {
    id: 'trending',
    label: 'Trending',
  },
  {
    id: 'recorded',
    label: 'Recorded',
  },
  {
    id: 'scheduled',
    label: 'Scheduled',
  },
];

const ShowsTabContent: FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [getShows, { isFetching }] = useLazyGetAllShowsQuery();
  const showsContainerRef = useRef<HTMLDivElement>(null);
  const ids = useShowsIds();
  const mayLoadMore = useShowsMayloadMore();
  const { isMobile, isTablet } = useScreenSizes();
  const { debouncedSearchValue, onSearch, searchValue } = useSearch();

  const loadShows = useCallback(
    ({ isRefresh, lastId }: { isRefresh: boolean; lastId?: string }) => {
      if (selectedFilter === 'all') {
        getShows({
          isRefresh,
          lastId,
          search: debouncedSearchValue,
        });
      } else {
        getShows({
          isRefresh,
          lastId,
          filters: [selectedFilter],
          search: debouncedSearchValue,
        });
      }
    },
    [getShows, selectedFilter, debouncedSearchValue],
  );

  const onLoadMore = useCallback(() => {
    if (mayLoadMore) {
      loadShows({ isRefresh: false, lastId: ids[ids.length - 1] });
    }
  }, [mayLoadMore, loadShows, ids]);

  useEffect(() => {
    loadShows({ isRefresh: true });
  }, [loadShows]);

  useScroll({ onLoadMore, containerRef: showsContainerRef });

  return (
    <Container>
      <Body>
        <StickyHeader>
          {(isMobile || isTablet) && (
            <InputContainer>
              <SearchInput
                placeholder={TEXT_INPUT_PLACEHOLDERS.SEARCH}
                value={searchValue}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onSearch(event.target.value)
                }
                className='search-input'
                isSkewed
                customIcon={
                  searchValue ? (
                    <CloseIcon size={20} onClick={() => onSearch('')} />
                  ) : (
                    <SearchIcon size={20} />
                  )
                }
              />
            </InputContainer>
          )}

          <FiltersContainer>
            {filters.map((filter) => (
              <FilterButton
                key={filter.id}
                isGradient={filter.id === selectedFilter}
                onClick={() => setSelectedFilter(filter.id)}
              >
                {filter.label}
              </FilterButton>
            ))}
          </FiltersContainer>
        </StickyHeader>

        <ShowsWrapper>
          <ShowsContainer ref={showsContainerRef} $isEmpty={!ids.length}>
            {!isFetching && !ids.length ? (
              <EmptyState>
                {!debouncedSearchValue ? emptyText.shows : emptyText.noResults}
              </EmptyState>
            ) : (
              ids.map((id: string) => <ShowCard key={id} showId={id} />)
            )}

            {isFetching && <Loader size={'large'} />}
          </ShowsContainer>
        </ShowsWrapper>
      </Body>
    </Container>
  );
};

export default memo(ShowsTabContent);
